import { ScheduleDays } from '@core/models/schedule-day.model';
import { IShopSchedule } from '@core/models/shop.model';
import { TagAtomComponent } from '../tag-atom/tag-atom.component';
import dayjs from 'dayjs';
import { DAYS_SCHEDULE } from '@config/values/schedule-days.values';
import { VenueUtils } from '@shared/utils/venue/venue.utils';
import { ScheduleKeys } from '@config/keys/shop.keys';
import { ScheduleDayKeys } from '@config/keys/schedule-day.keys';

export interface TimeSlot {
  start: number;
  end: number;
  address?: string;
  allowed_distribution_modes?: string[];
  isClosed: boolean;
}

export interface IDisplaySchedule {
  isToday: boolean;
  date: Date;
  day: ScheduleDays;
  timeSlots: Array<TimeSlot>;
  statusColor: TagAtomComponent['color'];
  status: 'open' | 'reducedHours' | 'closed' | 'exceptionallyClosed';
}

export function createCalendarOfTheNext90Days({
  schedules,
  scheduleStatusColorMap,
}: {
  schedules: IShopSchedule[];
  scheduleStatusColorMap: Record<IDisplaySchedule['status'], TagAtomComponent['color']>;
}) {
  const schedulesWithoutOutdatedSchedules = filterOutdatedSchedules({ schedules });

  const next90CalendarDays = generateNext90Days();
  return next90CalendarDays.map(calendarDay => {
    const schedulesForDay = filterSchedulesAppliedOnSpecificDate({
      schedulesWithoutOutdatedSchedules,
      day: calendarDay.day,
      date: calendarDay.date,
    });
    const openedSchedules = schedulesForDay.filter(schedule => schedule.opened);

    const closedSchedules = schedulesForDay
      .filter(schedule => !schedule.opened)
      .sort((slot1, slot2) => slot1.start - slot2.start);

    return buildDayScheduleDetails({
      openedSchedules,
      closedSchedules,
      day: calendarDay.day,
      date: calendarDay.date,
      scheduleStatusColorMap,
    });
  });
}

function filterOutdatedSchedules({ schedules }: { schedules: IShopSchedule[] }) {
  return schedules.filter(schedule => {
    const availabilityEndDateEndBeforeNow =
      schedule.availability_end_date && new Date(schedule.availability_end_date) < new Date();

    return !availabilityEndDateEndBeforeNow;
  });
}

function generateNext90Days() {
  const calendarDays = [];
  const today = dayjs();

  for (let i = 0; i < 90; i++) {
    const currentDay = today.add(i, 'day');
    calendarDays.push({
      dayNumber: currentDay.date(),
      month: currentDay.month() + 1, // month() is 0-indexed
      date: currentDay.toDate(),
      day: DAYS_SCHEDULE.find(day => day.index === currentDay.day()) as ScheduleDays,
    });
  }
  return calendarDays;
}

function buildDayScheduleDetails({
  openedSchedules,
  closedSchedules,
  day,
  date,
  scheduleStatusColorMap,
}: {
  openedSchedules: IShopSchedule[];
  closedSchedules: IShopSchedule[];
  day: ScheduleDays;
  date: Date;
  scheduleStatusColorMap: Record<IDisplaySchedule['status'], TagAtomComponent['color']>;
}) {
  const timeSlots = evaluateScheduleIntervals({ openedSchedules, closedSchedules }).map(slot =>
    createTimeSlot({ slot, day }),
  );
  const status = evaluateDayScheduleStatus(timeSlots);
  return {
    day,
    isToday: dayjs(date).isToday(),
    date,
    timeSlots,
    status,
    statusColor: scheduleStatusColorMap[status],
  } satisfies IDisplaySchedule;
}

function filterSchedulesAppliedOnSpecificDate({
  schedulesWithoutOutdatedSchedules,
  day,
  date,
}: {
  schedulesWithoutOutdatedSchedules: IShopSchedule[];
  day: ScheduleDays;
  date: Date;
}) {
  return schedulesWithoutOutdatedSchedules.filter(schedule => {
    const isScheduleAppliedOnThisWeekDay = schedule.start >= day.start && schedule.end <= day.end;
    if (!isScheduleAppliedOnThisWeekDay) {
      return false;
    }

    if (schedule.availability_start_date && new Date(schedule.availability_start_date) > date) {
      return false;
    }
    if (schedule.availability_end_date && new Date(schedule.availability_end_date) < date) {
      return false;
    }

    return true;
  });
}

function evaluateDayScheduleStatus(
  timeSlots: {
    isClosed: boolean;
  }[],
): IDisplaySchedule['status'] {
  const hasNoSchedule = timeSlots.length === 0;
  if (hasNoSchedule) {
    return 'closed';
  }
  const hasOnlyClosedSchedules = timeSlots.every(slot => slot.isClosed);
  if (hasOnlyClosedSchedules) {
    return 'exceptionallyClosed';
  }

  const hasOnlyOpenSchedules = timeSlots.every(slot => !slot.isClosed);
  if (hasOnlyOpenSchedules) {
    return 'open';
  }
  return 'reducedHours';
}

function createTimeSlot({
  slot,
  day,
}: {
  slot: IShopSchedule & { isClosed: boolean };
  day: ScheduleDays;
}) {
  const address = VenueUtils.getVenueAddressString(slot[ScheduleKeys.Venue]);
  const allTheDaySlotTimeDiff = slot[ScheduleKeys.End] - slot[ScheduleKeys.Start] === 1440 ? 1 : 0; // enable to display 00h00 - 23h59 for full day slot

  const timeDiff = day[ScheduleDayKeys.Index] * 1440;

  return {
    start: slot[ScheduleKeys.Start] - timeDiff,
    end: slot[ScheduleKeys.End] - timeDiff - allTheDaySlotTimeDiff,
    address,
    allowed_distribution_modes: slot.allowed_distribution_modes?.map(d => d.distribution_mode),
    isClosed: slot.isClosed,
  } satisfies TimeSlot;
}

function evaluateScheduleIntervals({
  openedSchedules,
  closedSchedules,
}: {
  openedSchedules: IShopSchedule[];
  closedSchedules: IShopSchedule[];
}): (IShopSchedule & { isClosed: boolean })[] {
  let openedSchedulesRecursive = [...openedSchedules].sort(
    (slot1, slot2) => slot1.start - slot2.start,
  );
  const result = [];
  while (openedSchedulesRecursive.length > 0) {
    // if the currentSchedule segment has no closedSchedule during his periods it will be added to the result
    // if their is a closed schedule during the currentSchedule segment:
    // if the currentSchedule segment is totally wrapped in a closed schedule, it will be added to the result and marked as closed
    // if the currentSchedule segment is partially wrapped in a closed schedule, it will be split into two segments and the new segments will be evaluated in the next iteration
    const currentSchedule = openedSchedulesRecursive.shift();

    if (!currentSchedule) {
      continue;
    }

    const activeClosedSchedules = closedSchedules
      .filter(
        closedSchedule =>
          (closedSchedule.start >= currentSchedule.start &&
            closedSchedule.start < currentSchedule.end) ||
          (closedSchedule.end > currentSchedule.start &&
            closedSchedule.end <= currentSchedule.end) ||
          (closedSchedule.start < currentSchedule.start &&
            closedSchedule.end > currentSchedule.end),
      )
      .sort((slot1, slot2) => slot1.start - slot2.start);

    const hasClosingSchedule = activeClosedSchedules.length > 0;
    if (!hasClosingSchedule) {
      result.push({ ...currentSchedule, isClosed: false });
      continue;
    }

    const isCurrentScheduleWrappedInAClosedSchedule = closedSchedules.some(closedSchedule => {
      return (
        currentSchedule.start >= closedSchedule.start && currentSchedule.end <= closedSchedule.end
      );
    });

    if (isCurrentScheduleWrappedInAClosedSchedule) {
      result.push({ ...currentSchedule, isClosed: true });
      continue;
    }

    // Will generate a new opened schedule segment, that will be evaluated in the next iteration
    for (let i = 0; i < activeClosedSchedules.length; i++) {
      const { start: closedStart, end: closedEnd } = activeClosedSchedules[i];
      const isCurrentScheduleStartBeforeClosingSchedule = currentSchedule.start < closedStart;
      const isCurrentScheduleEndAfterClosingSchedule = currentSchedule.end > closedEnd;

      if (isCurrentScheduleStartBeforeClosingSchedule) {
        result.push({
          ...currentSchedule,
          end: closedStart,
          isClosed: false,
        });
        openedSchedulesRecursive = [
          {
            ...currentSchedule,
            start: closedStart,
          },
          ...openedSchedulesRecursive,
        ];
        break;
      }

      if (isCurrentScheduleEndAfterClosingSchedule) {
        result.push({
          ...currentSchedule,
          end: closedEnd,
          isClosed: true,
        });
        openedSchedulesRecursive = [
          {
            ...currentSchedule,
            start: closedEnd,
          },
          ...openedSchedulesRecursive,
        ];
        break;
      }
    }
  }
  return result;
}
